import { clubStorage } from './club-session';
/**
 * Market & Transfers State (Microservicio de Mercado y Fichajes)
 * Gestiona negociaciones de traspasos, pago de cláusulas de rescisión
 * y contrataciones de agentes libres según el presupuesto del club.
 */
import { Injectable, signal, computed, inject } from '@angular/core';
import {
  TransferOffer,
  LedgerEntry,
  MarketOfferResponse,
  Player,
  BackendActiveOffersResponse,
  BackendHistoricalOffersResponse,
  PortfolioItem,
  PortfolioResponse,
  ReleaseClauseAttemptPayload,
  ReleaseClauseAttemptResponse,
  ContractRenewalPayload,
  ContractRenewalResponse,
} from '../models/types';
import { evaluatePlayerContractAcceptance } from '../simulation/simulation';
import { portfolioSimulation } from '../simulation/portfolio-simulation';
import { marketApi } from '../http/market.api';
import { getMarketOfferErrorMessage } from '../utils/currency.utils';
import { UiState } from './ui.state';
import { SquadState } from './squad.state';

const STORAGE_KEY = 'football_manager_real_v01';

@Injectable({
  providedIn: 'root',
})
export class MarketState {
  private readonly ui = inject(UiState);
  private readonly squad = inject(SquadState);

  readonly transferOffers = signal<TransferOffer[]>([]);
  readonly activeOffersData = signal<BackendActiveOffersResponse | null>(null);
  readonly isActiveOffersLoading = signal<boolean>(false);
  readonly activeOffersError = signal<string | null>(null);

  readonly historicalOffersData = signal<BackendHistoricalOffersResponse | null>(null);
  readonly isHistoricalOffersLoading = signal<boolean>(false);
  readonly historicalOffersError = signal<string | null>(null);

  readonly portfolioItems = signal<PortfolioItem[]>([]);
  readonly isPortfolioLoading = signal<boolean>(false);
  readonly portfolioError = signal<string | null>(null);
  readonly pendingReleaseClauseAttempts = signal<ReleaseClauseAttemptResponse[]>([]);
  readonly pendingContractRenewals = signal<ContractRenewalResponse[]>([]);

  readonly portfolioCount = computed(() => this.portfolioItems().length);
  readonly hasMaxPortfolio = computed(() => this.portfolioItems().length >= 3);

  readonly watchlistPlayerIds = signal<string[]>(this.getInitialWatchlist());

  /**
   * Resetea el estado completo de ofertas y mercado al cambiar de usuario
   */
  private activeRequest = 0;
  private historyRequest = 0;

  constructor() {
    this.clearOfferCache();
  }

  private clearOfferCache(): void {
    if (typeof clubStorage === 'undefined') return;
    for (const suffix of ['offers', 'active_offers', 'historical_offers']) {
      try { clubStorage.removeItem(`${STORAGE_KEY}_${suffix}`); } catch {}
    }
  }

  resetMarketState(): void {
    ++this.activeRequest;
    ++this.historyRequest;
    this.isActiveOffersLoading.set(false);
    this.isHistoricalOffersLoading.set(false);
    this.clearOfferCache();
    this.transferOffers.set([]);
    this.activeOffersData.set(null);
    this.activeOffersError.set(null);
    this.historicalOffersData.set(null);
    this.historicalOffersError.set(null);
    this.watchlistPlayerIds.set([]);
  }

  /**
   * Envía una oferta formal al endpoint POST /api/market/listings/{listingId}/players/{playerInstanceId}/offers
   */
  async submitPlayerOffer(
    listingId: string,
    playerInstanceId: string,
    payload: {
      transferAmount: number;
      annualSalary: number;
      releaseClause: number;
      termSeasons: number;
    },
    player?: Player
  ): Promise<MarketOfferResponse | null> {
    const userC = this.squad.userClub();
    const targetPlayer =
      player ||
      this.squad.allPlayers().find(
        (p) => p.playerInstanceId === playerInstanceId || p.id === playerInstanceId
      );

    try {
      const res = await marketApi.submitPlayerOffer(listingId, playerInstanceId, payload);

      // Si el backend responde con status FAILED (ej. en 200 con cuerpo fallido)
      if (res && res.status === 'FAILED') {
        const failure = res.failure;
        const failureErr: any = new Error(failure?.message || 'Error al procesar la oferta.');
        failureErr.status = 409;
        failureErr.failure = failure;
        failureErr.responseBody = res;
        throw failureErr;
      }

      const playerName = targetPlayer?.name || 'Futbolista';
      const newOffer: TransferOffer = {
        id: res.id || `off-${Date.now()}`,
        playerId: targetPlayer?.id || playerInstanceId,
        fromClubId: userC.id,
        toClubId: targetPlayer?.clubId || null,
        transferFeeM: Number((payload.transferAmount / 1_000_000).toFixed(2)),
        offeredSalaryM: Number((payload.annualSalary / 1_000_000).toFixed(2)),
        contractDurationYears: payload.termSeasons,
        offeredReleaseClauseM: Number((payload.releaseClause / 1_000_000).toFixed(2)),
        status: res.status || 'SUBMITTED',
        type: targetPlayer?.clubId ? 'CLUB_OFFER' : 'FREE_AGENT',
        createdOn: res.submittedAt || new Date().toISOString(),
        listingId: res.listingId || listingId,
        playerInstanceId: res.playerInstanceId || playerInstanceId,
        cutoffAt: res.cutoffAt,
        reservationId: res.reservationId,
        playerName,
        position: targetPlayer?.position,
        estimatedPlayerDecision: res.estimatedPlayerDecision,
      };

      this.transferOffers.update((prev) => {
        const next = [newOffer, ...prev];
        return next;
      });

      this.ui.showNotification(
        `¡Oferta formal de ${(payload.transferAmount / 1_000_000).toFixed(2)} M€ enviada con éxito por ${playerName}! (Estado: ${res.status || 'SUBMITTED'})`,
        'success'
      );

      return res;
    } catch (err: any) {
      const msg = getMarketOfferErrorMessage(err);
      this.ui.showNotification(msg, 'error');
      err.translatedMessage = msg;
      throw err;
    }
  }

  /**
   * Actualiza / modifica una oferta formal existente mediante llamada PUT a /api/market/offers/{offerId}.
   * Mismo endpoint base que cancelar oferta pero sin /cancellation.
   */
  async updatePlayerOffer(
    offerIdOrListingId: string,
    payloadOrPlayerInstanceId: any,
    listingIdOrPayload?: any,
    offerIdOrPlayerInstanceId?: any,
    playerObj?: Player
  ): Promise<MarketOfferResponse | null> {
    let offerId = '';
    let payload: {
      transferAmount: number;
      annualSalary: number;
      releaseClause: number;
      termSeasons: number;
    };
    let listingId = '';
    let playerInstanceId = '';
    let player = playerObj;

    if (
      payloadOrPlayerInstanceId &&
      typeof payloadOrPlayerInstanceId === 'object' &&
      'transferAmount' in payloadOrPlayerInstanceId
    ) {
      offerId = offerIdOrListingId;
      payload = payloadOrPlayerInstanceId;
      listingId = listingIdOrPayload || '';
      playerInstanceId = offerIdOrPlayerInstanceId || '';
    } else {
      listingId = offerIdOrListingId;
      playerInstanceId = payloadOrPlayerInstanceId;
      payload = listingIdOrPayload;
      offerId = offerIdOrPlayerInstanceId || '';
    }

    const userC = this.squad.userClub();
    const targetPlayer =
      player ||
      this.squad.allPlayers().find(
        (p) => p.playerInstanceId === playerInstanceId || p.id === playerInstanceId
      );

    try {
      const res = await marketApi.updatePlayerOffer(offerId, payload, listingId, playerInstanceId);

      // Si el backend responde con status FAILED (ej. en 200 con cuerpo fallido)
      if (res && res.status === 'FAILED') {
        const failure = res.failure;
        const failureErr: any = new Error(failure?.message || 'Error al actualizar la oferta.');
        failureErr.status = 409;
        failureErr.failure = failure;
        failureErr.responseBody = res;
        throw failureErr;
      }

      const playerName = targetPlayer?.name || 'Futbolista';
      const updatedOfferId = offerId || res.id || `off-${Date.now()}`;

      this.transferOffers.update((prev) => {
        const exists = prev.some(
          (o) =>
            o.id === updatedOfferId ||
            (o.listingId === listingId && o.playerInstanceId === playerInstanceId)
        );
        let next: TransferOffer[];
        if (exists) {
          next = prev.map((o) => {
            if (
              o.id === updatedOfferId ||
              (o.listingId === listingId && o.playerInstanceId === playerInstanceId)
            ) {
              return {
                ...o,
                transferFeeM: Number((payload.transferAmount / 1_000_000).toFixed(2)),
                offeredSalaryM: Number((payload.annualSalary / 1_000_000).toFixed(2)),
                contractDurationYears: payload.termSeasons,
                offeredReleaseClauseM: Number((payload.releaseClause / 1_000_000).toFixed(2)),
                status: res.status || 'SUBMITTED',
                estimatedPlayerDecision: res.estimatedPlayerDecision,
              };
            }
            return o;
          });
        } else {
          const newOffer: TransferOffer = {
            id: updatedOfferId,
            playerId: targetPlayer?.id || playerInstanceId,
            fromClubId: userC.id,
            toClubId: targetPlayer?.clubId || null,
            transferFeeM: Number((payload.transferAmount / 1_000_000).toFixed(2)),
            offeredSalaryM: Number((payload.annualSalary / 1_000_000).toFixed(2)),
            contractDurationYears: payload.termSeasons,
            offeredReleaseClauseM: Number((payload.releaseClause / 1_000_000).toFixed(2)),
            status: res.status || 'SUBMITTED',
            type: targetPlayer?.clubId ? 'CLUB_OFFER' : 'FREE_AGENT',
            createdOn: res.submittedAt || new Date().toISOString(),
            listingId: res.listingId || listingId,
            playerInstanceId: res.playerInstanceId || playerInstanceId,
            cutoffAt: res.cutoffAt,
            reservationId: res.reservationId,
            playerName,
            position: targetPlayer?.position,
            estimatedPlayerDecision: res.estimatedPlayerDecision,
          };
          next = [newOffer, ...prev];
        }
        return next;
      });

      this.fetchActiveOffers('active').catch(() => {});

      this.ui.showNotification(
        `¡Oferta modificada con éxito por ${playerName}! Nueva cuantía: ${(payload.transferAmount / 1_000_000).toFixed(2)} M€`,
        'success'
      );

      return res;
    } catch (err: any) {
      const msg = getMarketOfferErrorMessage(err);
      this.ui.showNotification(msg, 'error');
      err.translatedMessage = msg;
      throw err;
    }
  }

  /**
   * Cancela una oferta formal de traspaso llamando a PUT /api/market/offers/{offerId}/cancellation
   */
  async cancelOffer(offerId: string): Promise<boolean> {
    try {
      await marketApi.cancelOffer(offerId);

      // Eliminar de transferOffers locales
      this.transferOffers.update((prev) => {
        const next = prev.filter((o) => o.id !== offerId);
        return next;
      });

      // Actualizar activeOffersData en memoria si está cargada
      this.activeOffersData.update((prev) => {
        if (!prev) return prev;
        if (Array.isArray(prev)) {
          return prev.filter((o) => o.id !== offerId);
        } else if (typeof prev === 'object') {
          const raw = prev as any;
          const offers = Array.isArray(raw.offers) ? raw.offers.filter((o: any) => o.id !== offerId) : raw.offers;
          const players = Array.isArray(raw.players) ? raw.players.filter((p: any) => p.id !== offerId && p.offerId !== offerId) : raw.players;
          return {
            ...prev,
            offers,
            players,
          };
        }
        return prev;
      });

      this.ui.showNotification('Oferta cancelada con éxito.', 'info');
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Error al cancelar la oferta.';
      this.ui.showNotification(msg, 'error');
      throw err;
    }
  }

  makeTransferOffer(
    playerId: string,
    transferFeeM: number,
    salaryM: number,
    durationYears: number,
    clauseM: number,
    isBuyout: boolean = false,
    signingBonusM: number = 0
  ): { success: boolean; message: string } {
    const player = this.squad.allPlayers().find((p) => p.id === playerId);
    const userC = this.squad.userClub();

    if (!player) return { success: false, message: 'Jugador no encontrado' };

    const isFreeAgent = player.clubId === null;
    const upfrontFee = isFreeAgent ? signingBonusM : transferFeeM;
    const finalClause = isFreeAgent ? 0 : clauseM; // En los agentes libres no habrá cláusula de rescisión
    const totalCost = +(upfrontFee + salaryM).toFixed(3);

    if (userC.economy.availableBalance < totalCost) {
      return {
        success: false,
        message: isFreeAgent
          ? 'Presupuesto insuficiente para cubrir la prima de fichaje y el salario.'
          : 'Presupuesto insuficiente para cubrir traspaso y salario.',
      };
    }

    const evaluation = evaluatePlayerContractAcceptance(
      player,
      salaryM,
      durationYears,
      finalClause,
      userC.division,
      signingBonusM
    );

    if (!evaluation.accepted) {
      return {
        success: false,
        message: `El jugador rechazó la propuesta: ${evaluation.reason}`,
      };
    }

    // Process accepted transfer
    const newBalance = +(userC.economy.availableBalance - totalCost).toFixed(3);
    const conceptText = isFreeAgent
      ? `Fichaje como agente libre de ${player.name} (${upfrontFee > 0 ? upfrontFee.toFixed(2) + 'M€ prima fichaje + ' : ''}${salaryM.toFixed(2)}M€ ficha anual)`
      : `Fichaje de ${player.name} (${transferFeeM}M€ traspaso + ${salaryM}M€ ficha)`;

    const newLedgerEntry: LedgerEntry = {
      id: `led-trf-${Date.now()}`,
      timestamp: new Date().toISOString(),
      type: 'GASTO',
      amount: totalCost,
      concept: conceptText,
      referenceId: player.id,
      runningBalance: newBalance,
    };

    // Update player
    this.squad.allPlayers.update((prev) =>
      prev.map((p) =>
        p.id === playerId
          ? {
              ...p,
              clubId: userC.id,
              isAmateur: false,
              contract: {
                salary: salaryM,
                durationYears,
                releaseClause: finalClause,
                acquisitionCost: upfrontFee,
                signingBonus: isFreeAgent ? signingBonusM : 0,
                startSeason: 1,
                endSeason: 1 + durationYears,
              },
            }
          : p
      )
    );

    // Update user club balance
    this.squad.clubs.update((prevClubs) =>
      prevClubs.map((c) =>
        c.id === userC.id
          ? {
              ...c,
              economy: {
                ...c.economy,
                availableBalance: newBalance,
                ledger: [newLedgerEntry, ...c.economy.ledger],
              },
            }
          : c
      )
    );

    this.ui.showNotification(
      isFreeAgent
        ? `¡Fichaje completado! ${player.name} se incorpora a tu plantilla como agente libre.`
        : `¡Fichaje completado! ${player.name} se une a ${userC.name}`,
      'success'
    );
    return { success: true, message: 'Fichaje oficializado con éxito.' };
  }

  buyoutReleaseClause(playerId: string): { success: boolean; message: string } {
    const player = this.squad.allPlayers().find((p) => p.id === playerId);
    if (!player) return { success: false, message: 'Jugador no encontrado' };

    const clause = player.contract.releaseClause || 50;
    const salary = +(player.contract.salary * 1.25).toFixed(2);
    const newClause = +(clause * 1.5).toFixed(2);

    return this.makeTransferOffer(playerId, clause, salary, 3, newClause, true);
  }

  refreshDailyFreeAgents(): void {
    this.ui.showNotification('Mercado libre actualizado', 'info');
  }

  async fetchActiveOffers(status: string = 'active'): Promise<BackendActiveOffersResponse | null> {
    const request = ++this.activeRequest;
    const clubId = this.squad.userClub?.()?.id;
    const current = () => request === this.activeRequest && this.squad.userClub?.()?.id === clubId;
    const knownLocalIds = new Set(this.transferOffers().map(offer => offer.id));
    this.isActiveOffersLoading.set(true);
    this.activeOffersError.set(null);

    try {
      const res = await marketApi.getActiveOffers(status);
      if (!current()) return this.activeOffersData();
      const rows = Array.isArray(res) ? res : (res as any)?.offers ?? (res as any)?.players;
      if (!Array.isArray(rows)) throw new Error('Respuesta de ofertas activas no válida.');
      this.activeOffersData.set(res);
      // Reconcile cached submissions so resolved offers cannot keep a player locked.
      // Preserve submissions made while this request was in flight.
      if (status === 'active') {
        const activeIds = new Set(rows.map((offer: any) => offer.offerId || offer.id));
        this.transferOffers.update(offers => offers.filter(offer =>
          !knownLocalIds.has(offer.id) || activeIds.has(offer.id)));
      }
      return res;
    } catch (err: any) {
      if (!current()) return this.activeOffersData();
      const msg = err?.message || 'Error al obtener las ofertas activas del servidor.';
      this.activeOffersError.set(msg);
      return this.activeOffersData();
    } finally {
      if (current()) this.isActiveOffersLoading.set(false);
    }
  }

  async fetchHistoricalOffers(page: number = 0, size: number = 50): Promise<BackendHistoricalOffersResponse | null> {
    const request = ++this.historyRequest;
    const clubId = this.squad.userClub?.()?.id;
    const current = () => request === this.historyRequest && this.squad.userClub?.()?.id === clubId;
    this.isHistoricalOffersLoading.set(true);
    this.historicalOffersError.set(null);

    try {
      const res = await marketApi.getOffersHistory(page, size);
      if (!current()) return this.historicalOffersData();
      this.historicalOffersData.set(res);
      return res;
    } catch (err: any) {
      if (!current()) return this.historicalOffersData();
      const msg = err?.message || 'Error al obtener el historial de ofertas del servidor.';
      this.historicalOffersError.set(msg);
      return this.historicalOffersData();
    } finally {
      if (current()) this.isHistoricalOffersLoading.set(false);
    }
  }

  async fetchPortfolio(clubId?: string, forceRefresh: boolean = false): Promise<PortfolioItem[]> {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';
    this.isPortfolioLoading.set(true);
    this.portfolioError.set(null);
    try {
      let rawResponse: any;
      try {
        rawResponse = await marketApi.getPortfolio(targetClubId);
      } catch (err: any) {
        rawResponse = portfolioSimulation.getPortfolio(
          targetClubId,
          (id) => this.squad.allPlayers().find((p) => p.id === id || p.playerInstanceId === id)
        );
      }

      // El endpoint puede devolver directamente un array [...] o un objeto { items: [...] }
      const rawList: any[] = Array.isArray(rawResponse)
        ? rawResponse
        : (Array.isArray(rawResponse?.items) ? rawResponse.items : []);

      const normalizePos = (raw: string | undefined): string => {
        const p = (raw || '').toUpperCase();
        if (p.includes('FORWARD') || p.includes('DEL') || p.includes('ATTACK')) return 'DEL';
        if (p.includes('MIDFIELD') || p.includes('MED')) return 'MED';
        if (p.includes('DEFEND') || p.includes('DEF')) return 'DEF';
        if (p.includes('GOAL') || p.includes('POR') || p.includes('GK')) return 'POR';
        return raw || 'MED';
      };

      const items: PortfolioItem[] = rawList.map((raw: any) => {
        const pId = raw.playerInstanceId || raw.playerId || raw.id;
        const knownPlayer =
          portfolioSimulation.getRegisteredPlayer?.(pId) ||
          this.squad.allPlayers().find((p) => p.id === pId || p.playerInstanceId === pId || (raw.playerDefinitionId && p.playerDefinitionId === raw.playerDefinitionId)) ||
          raw.player;

        const displayName = raw.displayName || raw.name || knownPlayer?.name || 'Futbolista';
        const position = normalizePos(raw.position || knownPlayer?.position || 'MED');
        const age = raw.age ?? knownPlayer?.age ?? 24;
        const overall = raw.overall ?? knownPlayer?.overall ?? (raw.rating != null ? Math.round(raw.rating) : 75);
        const averagePoints = raw.averagePoints !== undefined ? raw.averagePoints : (knownPlayer?.averagePoints ?? null);
        const last3AveragePoints = raw.last3AveragePoints !== undefined ? raw.last3AveragePoints : (knownPlayer?.last3AveragePoints ?? null);
        const type = raw.type || knownPlayer?.playerType || 'REAL';

        const rawMarketVal = raw.marketValue != null ? raw.marketValue : (raw.currentMarketValue != null ? raw.currentMarketValue : (knownPlayer?.currentMarketValue ? knownPlayer.currentMarketValue * 1_000_000 : 5_000_000));
        const marketValueEur = rawMarketVal < 10_000 ? Math.round(rawMarketVal * 1_000_000) : rawMarketVal;

        const sellerClub = this.squad.clubs().find(c => c.id === (raw.sellerClubId || raw.ownerClubId));
        const realClub = raw.realClub || knownPlayer?.realClub || (sellerClub ? { id: sellerClub.id, name: sellerClub.name, shortName: sellerClub.code } : undefined);
        const clubName = realClub?.name || sellerClub?.name || raw.clubName || knownPlayer?.clubName || 'Club rival';
        const clubCode = realClub?.shortName || sellerClub?.code || raw.clubCode || knownPlayer?.realClub?.shortName;
        const crestUrl = clubCode ? `crest/${clubCode.toUpperCase()}.png` : (raw.crestUrl || null);

        const isRevealed = raw.revealed === true || raw.status === 'DISCOVERED' || raw.discoveryStatus === 'DISCOVERED';
        const rawClause = raw.releaseClause != null ? raw.releaseClause : (raw.discoveredClause != null ? raw.discoveredClause : null);
        const clauseEur = rawClause != null ? (rawClause < 10_000 ? Math.round(rawClause * 1_000_000) : rawClause) : null;

        return {
          id: pId,
          playerId: pId,
          playerInstanceId: pId,
          playerDefinitionId: raw.playerDefinitionId || knownPlayer?.playerDefinitionId,
          name: displayName,
          displayName: displayName,
          position,
          age,
          overall,
          averagePoints,
          last3AveragePoints,
          type,
          marketValue: marketValueEur,
          currentMarketValue: marketValueEur,
          expectedVersion: raw.expectedVersion ?? 0,
          realClub,
          unavailabilities: raw.unavailabilities || knownPlayer?.unavailabilities || [],
          ownerClubId: raw.ownerClubId || raw.sellerClubId || raw.clubId || knownPlayer?.clubId,
          sellerClubId: raw.sellerClubId || raw.ownerClubId || raw.clubId || knownPlayer?.clubId,
          clubId: raw.ownerClubId || raw.sellerClubId || raw.clubId || knownPlayer?.clubId,
          clubName,
          clubCode,
          crestUrl,
          revealed: isRevealed,
          releaseClause: isRevealed ? clauseEur : null,
          status: isRevealed ? 'DISCOVERED' : 'PENDING',
          discoveryStatus: isRevealed ? 'DISCOVERED' : 'PENDING',
          discoveredClause: isRevealed ? clauseEur : null,
          lastKnownClause: isRevealed ? clauseEur : null,
          addedAt: raw.addedAt || new Date().toISOString(),
          discoveredAt: isRevealed ? (raw.discoveredAt || new Date().toISOString()) : null,
          player: {
            id: pId,
            playerInstanceId: pId,
            playerDefinitionId: raw.playerDefinitionId || knownPlayer?.playerDefinitionId,
            name: displayName,
            position,
            age,
            clubId: raw.ownerClubId || raw.sellerClubId || raw.clubId,
            clubName,
            crestUrl,
            marketValue: marketValueEur / 1_000_000,
            overall,
            currentClause: clauseEur,
          },
        };
      });

      this.portfolioItems.set(items);
      this.watchlistPlayerIds.set(items.map((i) => i.playerId));

      const attempts = portfolioSimulation.getReleaseClauseAttempts(targetClubId);
      this.pendingReleaseClauseAttempts.set(attempts);
      const renewals = portfolioSimulation.getContractRenewals(targetClubId);
      this.pendingContractRenewals.set(renewals);

      return items;
    } catch (err: any) {
      const msg = err?.message || 'Error al consultar la cartera.';
      this.portfolioError.set(msg);
      return [];
    } finally {
      this.isPortfolioLoading.set(false);
    }
  }

  async addToPortfolio(player: Player, clubId?: string): Promise<boolean> {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';

    // Criterio 2: No se permite añadir jugadores propios ni libres
    if (player.clubId === userC?.id || player.clubId === targetClubId) {
      this.ui.showNotification('No se permite añadir jugadores propios a la cartera.', 'error');
      return false;
    }
    if (!player.clubId) {
      this.ui.showNotification('No se permite añadir agentes libres a la cartera. Solo jugadores con contrato que pertenezcan a otro club.', 'error');
      return false;
    }

    // Criterio 1: Con tres jugadores en cartera, no se permite añadir un cuarto y se explica el límite
    if (this.portfolioCount() >= 3 && !this.isInWatchlist(player.id)) {
      this.ui.showNotification('Límite alcanzado: el club solo puede tener un máximo de 3 jugadores en cartera.', 'error');
      return false;
    }

    this.isPortfolioLoading.set(true);
    this.portfolioError.set(null);
    try {
      try {
        await marketApi.addToPortfolio(player.id, targetClubId, player);
      } catch (apiErr: any) {
        if (apiErr?.status === 409 || apiErr?.responseBody?.status === 409 || apiErr?.message?.includes('at most three')) {
          const detail = apiErr.responseBody?.detail || apiErr.message || 'Límite alcanzado: el club solo puede tener un máximo de 3 jugadores en cartera.';
          this.ui.showNotification(detail, 'error');
          return false;
        }
        portfolioSimulation.addToPortfolio(
          targetClubId,
          player.id,
          (id) => (id === player.id || id === player.playerInstanceId ? player : this.squad.allPlayers().find((p) => p.id === id || p.playerInstanceId === id)),
          userC?.id || targetClubId
        );
      }

      await this.fetchPortfolio(targetClubId, true);
      this.ui.showNotification(`¡${player.name} añadido a tu cartera! Cláusula pendiente de descubrir en el próximo cierre.`, 'success');
      return true;
    } catch (err: any) {
      const msg = err?.responseBody?.detail || err?.message || 'Error al añadir el jugador a la cartera.';
      this.portfolioError.set(msg);
      this.ui.showNotification(msg, 'error');
      return false;
    } finally {
      this.isPortfolioLoading.set(false);
    }
  }

  async removeFromPortfolio(playerId: string, clubId?: string): Promise<boolean> {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';
    const player = this.squad.allPlayers().find((p) => p.id === playerId || p.playerInstanceId === playerId);
    const pName = player?.name || 'Futbolista';

    this.isPortfolioLoading.set(true);
    try {
      try {
        await marketApi.removeFromPortfolio(playerId, targetClubId);
      } catch {
        portfolioSimulation.removeFromPortfolio(targetClubId, playerId);
      }
      await this.fetchPortfolio(targetClubId, true);
      this.ui.showNotification(`${pName} retirado de la cartera`, 'info');
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Error al retirar el jugador de la cartera.';
      this.ui.showNotification(msg, 'error');
      return false;
    } finally {
      this.isPortfolioLoading.set(false);
    }
  }

  async toggleWatchlist(playerId: string): Promise<boolean> {
    const userC = this.squad.userClub();
    const clubId = userC?.id || 'default';
    const player = this.squad.allPlayers().find((p) => p.id === playerId || p.playerInstanceId === playerId);
    const isCurrentlyIn = this.isInWatchlist(playerId);

    if (isCurrentlyIn) {
      return await this.removeFromPortfolio(playerId, clubId);
    } else {
      if (!player) {
        this.ui.showNotification('Futbolista no encontrado.', 'error');
        return false;
      }
      return await this.addToPortfolio(player, clubId);
    }
  }

  isInWatchlist(playerId: string): boolean {
    if (!playerId) return false;
    return this.portfolioItems().some((i) =>
      i.playerId === playerId ||
      i.playerInstanceId === playerId ||
      i.id === playerId ||
      (i.playerDefinitionId && i.playerDefinitionId === playerId)
    ) || this.watchlistPlayerIds().includes(playerId);
  }

  async submitReleaseClauseAttempt(
    payload: ReleaseClauseAttemptPayload,
    clubId?: string
  ): Promise<ReleaseClauseAttemptResponse> {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';
    const player = this.squad.allPlayers().find((p) => p.id === payload.playerId);
    const availableEur = Math.round((userC.economy.availableBalance ?? 0) * 1_000_000);

    let res: ReleaseClauseAttemptResponse;
    try {
      res = await marketApi.submitReleaseClauseAttempt(payload, targetClubId);
    } catch (err: any) {
      if (err?.status === 409 || err?.failure || err?.code === 'FUNDS_RESERVATION_REJECTED' || err?.code === 'CLAUSE_CHANGED') {
        throw err;
      }
      res = portfolioSimulation.submitReleaseClauseAttempt(
        targetClubId,
        payload,
        (id) => this.squad.allPlayers().find((p) => p.id === id),
        availableEur
      );
    }

    this.pendingReleaseClauseAttempts.update((prev) => [res, ...prev.filter((a) => a.id !== res.id)]);
    this.ui.showNotification(
      `¡Clausulazo presentado por ${player?.name || 'el jugador'}! La operación queda pendiente de resolución al cierre diario.`,
      'info'
    );
    return res;
  }

  async submitContractRenewal(
    payload: ContractRenewalPayload,
    clubId?: string
  ): Promise<ContractRenewalResponse> {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';
    const player = this.squad.allPlayers().find((p) => p.id === payload.playerId);

    const res = await marketApi.submitContractRenewal(payload, targetClubId);
    if (res.status === 'REJECTED') throw new Error('La propuesta de renovación ha sido rechazada o ya no está activa.');

    this.pendingContractRenewals.update((prev) => [res, ...prev.filter((r) => r.id !== res.id)]);
    this.ui.showNotification(
      res.status === 'ACCEPTED' ? 'La renovación ya se ha completado.' : `Propuesta de renovación enviada a ${player?.name || 'el jugador'}. Se resolverá en el cierre diario.`,
      'info'
    );
    return res;
  }

  resolveDailyMarket(clubId?: string): { discoveredCount: number; resolvedAttemptsCount: number; resolvedRenewalsCount: number; summary: string[] } {
    const userC = this.squad.userClub();
    const targetClubId = clubId || userC?.id || 'default';

    const result = portfolioSimulation.resolveDailyMarket({
      userClubId: targetClubId,
      allPlayers: this.squad.allPlayers(),
      updatePlayer: (updated) => {
        this.squad.allPlayers.update((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      },
      updateClubBalance: (targetClub, deltaEur, concept, referenceId) => {
        const deltaM = Number((deltaEur / 1_000_000).toFixed(3));
        this.squad.clubs.update((clubs) =>
          clubs.map((c) => {
            if (c.id !== targetClub) return c;
            const newBal = Number((c.economy.availableBalance + deltaM).toFixed(3));
            const newEntry: LedgerEntry = {
              id: `led-rca-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
              timestamp: new Date().toISOString(),
              type: deltaM >= 0 ? 'INGRESO' : 'GASTO',
              amount: Math.abs(deltaM),
              concept,
              referenceId,
              runningBalance: newBal,
            };
            return {
              ...c,
              economy: {
                ...c.economy,
                availableBalance: newBal,
                ledger: [newEntry, ...c.economy.ledger],
              },
            };
          })
        );
      },
    });

    void this.fetchPortfolio(targetClubId, true);

    if (result.summary.length > 0) {
      this.ui.showNotification(result.summary[0], 'info');
    }
    return result;
  }

  private saveWatchlist(list: string[]): void {
    if (typeof clubStorage !== 'undefined') {
      try {
        clubStorage.setItem(`${STORAGE_KEY}_watchlist`, JSON.stringify(list));
      } catch {}
    }
  }

  private getInitialWatchlist(): string[] {
    try {
      const saved = clubStorage.getItem(`${STORAGE_KEY}_watchlist`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }
}
